import type { ComponentType } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyPutDevicesUpdateStockedStatusQuery } from "store/api/adminApi"
import { useLazyUpdateWHDevicesStatusQuery } from "store/api/warehouseManagerApi"
import { useAppSelector } from "store/hooks"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconRefurbished } from "assets/svg/modal-icon-refurbished.svg"
import { ReactComponent as IconRepackaged } from "assets/svg/modal-icon-repackaged.svg"
import { ReactComponent as IconReturned } from "assets/svg/modal-icon-returned.svg"

import { UserRole } from "utils/checkRoleType"
import getErrorMessage from "utils/getErrorMessage"
import { useUserProfile } from "hooks/useUserProfile"
import { ModalLayout } from "layouts/ModalLayout"

export type VendorDeviceStatus = "repackaged" | "returned" | "refurbished"

interface Props {
  status: VendorDeviceStatus
  onCancel: () => void
  onSuccess: () => void
}

const statusDetails: Record<VendorDeviceStatus, { title: string; icon: ComponentType; message: string }> = {
  repackaged: {
    title: "Mark as Repackaged",
    icon: IconRepackaged,
    message:
      "By changing the status of the selected device(s) to “Repackaged”, you confirm that the device(s) are functional and have been repacked for future use. Are you sure you want to change the status of the selected device(s)?",
  },
  returned: {
    title: "Mark as Returned",
    icon: IconReturned,
    message:
      "By changing the status of the selected device(s) to “Returned”, you confirm that the device(s) have been returned to Nuve and are no longer part of your standard stock. Are you sure you want to change the status of the selected device(s)?",
  },
  refurbished: {
    title: "Mark as Refurbished",
    icon: IconRefurbished,
    message:
      "By changing the status of the selected device(s) to “Refurbished”, you confirm that the device(s) were repaired, tested, and approved for reuse. Are you sure you want to change the status of the selected device(s)?",
  },
}

export const ChangeDeviceStatusModalForVendor = ({ status, onCancel, onSuccess }: Props) => {
  const [triggerUpdateStockedStatus, { isFetching: isStockedStatusFetching }] =
    useLazyPutDevicesUpdateStockedStatusQuery()
  const [triggerUpdateWarehouseStatus, { isFetching: isWarehouseStatusFetching }] = useLazyUpdateWHDevicesStatusQuery()
  const isFetching = isStockedStatusFetching || isWarehouseStatusFetching
  const { userRole } = useUserProfile()
  const selectedDeviceIDs = useAppSelector(state => state.tempData.selectedDeviceIDs)
  const details = statusDetails[status]
  const Icon = details.icon

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    try {
      if (![UserRole.VendorAdmin, UserRole.WarehouseManager, UserRole.SalesManager].includes(userRole)) {
        toast.error("You are not authorized to change device status.")
        return
      }

      const triggerUpdateDeviceStatus =
        userRole === UserRole.WarehouseManager ? triggerUpdateWarehouseStatus : triggerUpdateStockedStatus
      const res = await triggerUpdateDeviceStatus({ device_ids: selectedDeviceIDs, phisical_status: status })
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
        return
      }

      toast.success("The status of the selected thermostat(s) was changed successfully!")
      onSuccess()
    } catch (error) {
      console.error(error)
    }
  }

  return createPortal(
    <ModalLayout width="450px" isFetching={isFetching} onConfirm={onSubmit} onCancel={onCancel}>
      <div className={classNames(styles.modalContent, styles.center)}>
        <Icon />
        <h3 className={styles.modalTitle}>{details.title}</h3>
        <div className={styles.modalMessage}>{details.message}</div>
      </div>
    </ModalLayout>,
    document.body,
  )
}
